import React, { useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import { 
  Box, 
  Button, 
  CircularProgress, 
  FormControl,
  Grid, 
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField, 
  Typography,
  styled
} from '@mui/material';
import { RootState, useAppDispatch, useAppSelector } from "../../../../state/state.tsx";
import { closeModal, toggleLoading } from "../../../../state/ModalSlice/ModalSlice.ts";
import CloseIcon from "@mui/icons-material/Close";
import { toast } from 'react-toastify';
import CountryCodeSelect, { 
  validatePhoneNumber, 
  getPhoneNumberPlaceholder, 
  formatPhoneNumber,
  countryCodes 
} from '../../Components/CountryCodeSelect';
import { collection, addDoc, Timestamp } from 'firebase/firestore';
import { db } from '../../../../Config/firebase';
import { selectEffectiveUID, selectUserSliceV2Data } from '../../../../state/UserStatesV2/userSliceV2';

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    borderRadius: '12px',
    padding: theme.spacing(2),
    minWidth: '400px',
    maxWidth: '500px',
  },
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '8px',
    '& fieldset': {
      borderColor: theme.palette.grey[300],
    },
    '&:hover fieldset': {
      borderColor: theme.palette.primary.main,
    },
    '&.Mui-focused fieldset': {
      borderColor: theme.palette.primary.main,
    },
  },
}));

const PhoneInputContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  gap: theme.spacing(2)
}));

const StyledCountryCodeSelect = styled(CountryCodeSelect)({
  '& .MuiOutlinedInput-root': {
    height: '40px',
    '& .MuiSelect-select': {
      padding: '8.5px 14px',
      height: '40px !important',
      display: 'flex',
      alignItems: 'center'
    }
  }
});

interface FormData {
  name: string;
  amount: string;
  date: string;
  category: string;
  method: string;
  description: string;
  countryCode: string;
  phoneNumber: string;
}

export default function RecordExpenseModal() {
  const open = useAppSelector((state: RootState) => state.ModalSlice.RecordExpenseModalOpen);
  const isLoading = useAppSelector((state: RootState) => state.ModalSlice.isLoading);
  const serviceProviderId = useAppSelector(selectEffectiveUID);
  const userData = useAppSelector(selectUserSliceV2Data);
  const dispatch = useAppDispatch();

  const [formData, setFormData] = useState<FormData>({
    name: '',
    amount: '',
    date: new Date().toISOString().split('T')[0],
    category: 'general',
    method: 'cash',
    description: '',
    countryCode: '+91',
    phoneNumber: ''
  });

  const handleClose = () => {
    dispatch(closeModal("RecordExpenseModalOpen"));
    setFormData({
      name: '',
      amount: '',
      date: new Date().toISOString().split('T')[0],
      category: 'general',
      method: 'cash',
      description: '',
      countryCode: '+91',
      phoneNumber: ''
    });
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({...formData, [e.target.name]: e.target.value});
  };

  const handleSelectChange = (e: React.ChangeEvent<{ name?: string; value: unknown }>) => {
    const name = e.target.name as string;
    const value = e.target.value as string;
    setFormData({...formData, [name]: value});
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    const country = countryCodes.find((c: { dialCode: string }) => c.dialCode === formData.countryCode);
    
    if (!country) return;

    // Remove any non-digit characters
    const cleanValue = value.replace(/\D/g, '');
    
    // Get max length based on country
    const maxLength = Array.isArray(country.phoneLength) 
      ? country.phoneLength[1] 
      : country.phoneLength;

    // Only update if within length limit
    if (cleanValue.length <= maxLength) {
      setFormData(prev => ({
        ...prev,
        phoneNumber: cleanValue
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name || !formData.phoneNumber || !formData.amount) {
      toast.error("Please fill in all required fields");
      return;
    }

    if (!validatePhoneNumber(formData.phoneNumber, formData.countryCode)) {
      toast.error(`Please enter a valid phone number for ${formData.countryCode}`);
      return;
    }

    if (isNaN(Number(formData.amount)) || Number(formData.amount) <= 0) {
      toast.error("Please enter a valid amount");
      return;
    }

    try {
      dispatch(toggleLoading(true));
      
      const transactionData = {
        payerName: formData.name,
        amount: Number(formData.amount),
        type: 'Debit',
        status: 'Completed',
        description: formData.description,
        createdAt: Timestamp.now(),
        phoneNumber: formData.phoneNumber,
        countryCode: formData.countryCode,
        recordedBy: userData?.uid || 'unknown'
      };

      const transactionsRef = collection(db, 'ServiceProviders', serviceProviderId, 'Adhoc');
      await addDoc(transactionsRef, transactionData);
      
      toast.success("Expense recorded successfully!");
      handleClose();
    } catch (error) {
      toast.error("Failed to record expense. Please try again.");
      console.error("Error recording expense:", error);
    } finally {
      dispatch(toggleLoading(false));
    }
  };

  return (
    <StyledDialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <Box 
        sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          mb: 3,
          pb: 2,
          borderBottom: '1px solid',
          borderColor: 'divider'
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600 }}>Record Expense/Payout</Typography>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </Box>
      
      <DialogContent sx={{ p: 0 }}>
        <Box component="form" onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <StyledTextField
                required
                fullWidth
                label="Paid To"
                name="name"
                value={formData.name}
                onChange={handleChange}
                disabled={isLoading}
                size="small"
              />
            </Grid>
            <Grid item xs={12}>
              <PhoneInputContainer>
                <StyledCountryCodeSelect
                  value={formData.countryCode}
                  onChange={(value) => setFormData({...formData, countryCode: value})}
                  label="Country Code"
                  required
                  fullWidth
                />
                <StyledTextField
                  required
                  fullWidth
                  label="Phone Number"
                  name="phoneNumber"
                  value={formData.phoneNumber}
                  onChange={handlePhoneChange}
                  placeholder={getPhoneNumberPlaceholder(formData.countryCode)}
                  disabled={isLoading}
                  size="small"
                  inputProps={{
                    inputMode: 'numeric',
                    pattern: '[0-9]*',
                    maxLength: (() => {
                      const country = countryCodes.find((c: { dialCode: string }) => c.dialCode === formData.countryCode);
                      if (!country) return 15;
                      return Array.isArray(country.phoneLength) ? country.phoneLength[1] : country.phoneLength;
                    })()
                  }}
                  helperText={`Format: ${getPhoneNumberPlaceholder(formData.countryCode)}`}
                />
              </PhoneInputContainer>
            </Grid>
            <Grid item xs={12}>
              <StyledTextField
                required
                fullWidth
                label="Amount"
                name="amount"
                type="number"
                value={formData.amount}
                onChange={handleChange}
                disabled={isLoading}
                size="small"
                InputProps={{
                  startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <StyledTextField
                fullWidth
                label="Description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                disabled={isLoading}
                multiline
                rows={3}
                size="small"
              />
            </Grid>
            <Grid item xs={12} sx={{ mt: 2 }}>
              <Button
                type="submit"
                variant="contained"
                color="secondary"
                fullWidth
                startIcon={isLoading ? <CircularProgress size={24} color="inherit" /> : null}
                disabled={isLoading}
                sx={{
                  py: 1.5,
                  borderRadius: '8px',
                  textTransform: 'none',
                  fontSize: '1rem'
                }}
              >
                {isLoading ? 'Recording...' : 'Record Expense'}
              </Button>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
    </StyledDialog>
  );
} 